/**
 * Centralised JSON-LD schema builders for ViratAstro EEAT + AI Search Authority.
 *
 * Single source of truth for:
 *   • Person       — Vivek Virat Mudgal (Founder)
 *   • Organization — ViratAstro
 *   • WebSite      — with SearchAction
 *   • Article      — for blog posts (with author Person @id reference)
 *   • FAQPage      — auto-generated when FAQs exist
 *   • BreadcrumbList
 *   • AboutPage
 *
 * All schemas are validated against schema.org and Google's Rich Results Test.
 * Re-use the @id values to interlink entities (essential for Knowledge Graph).
 */

export const SITE_URL = 'https://viratastro.cloud';
export const SITE_NAME = 'ViratAstro';
export const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

// Feb 12, 2026 N5/N13 Fix — @id normalization. Perplexity flagged mixed
// spellings across pages: `https://viratastro.cloud#organization` (no slash,
// from schemas.js) vs `https://viratastro.cloud/#organization` (with slash,
// from public/index.html). Now unified on the slash form so entity graph
// resolves consistently across static + runtime schemas.
export const VIVEK_ID = `${SITE_URL}/experts/vivek-virat-mudgal#person`;
export const VIVEK_URL = `${SITE_URL}/experts/vivek-virat-mudgal`;
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

// Vivek जी's verified social + business profiles (sameAs).
// Used by Person + Organization + LocalBusiness schemas for entity verification
// across Google Knowledge Graph, AI Overviews, Gemini, ChatGPT Search.
export const VIVEK_SAME_AS = [
  'https://www.instagram.com/astrovivekmudgal',
  'https://youtube.com/@astrovivekmudgal',
  'https://www.facebook.com/share/1KEWbgnpEE/',
  'https://g.page/r/CbtzPR3gc6gNEBM/review',  // Google Business Profile
];

// Google Business Profile review URL — exposed separately for "Leave a Review" CTAs
export const GOOGLE_BUSINESS_URL = 'https://g.page/r/CbtzPR3gc6gNEBM/review';
export const LOCAL_BUSINESS_ID = `${SITE_URL}/#localbusiness`;

export const VIVEK_PHOTO = '/media/founder/IMG_9620-removebg-preview.png';

// ─────────────────────────────────────────────────────────────────────────────
// Person Schema — Vivek Virat Mudgal (Founder)
// ─────────────────────────────────────────────────────────────────────────────
export const personSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': VIVEK_ID,
  name: 'Vivek Virat Mudgal',
  alternateName: ['Acharya Vivek Mudgal', 'Vivek Mudgal'],
  url: VIVEK_URL,
  image: VIVEK_PHOTO,
  jobTitle: 'Vedic Astrologer · Vastu Consultant · Parashari Jyotish Expert',
  description: 'Founder of ViratAstro with 33+ years of experience in Vedic Astrology, Parashari Jyotish, Vastu Shastra, and Spiritual Guidance. Featured on News Nation. Trusted by 33,000+ clients across 14+ countries.',
  worksFor: { '@id': ORG_ID },
  founder: { '@id': ORG_ID },
  knowsAbout: [
    'Vedic Astrology', 'Parashari Jyotish', 'Vastu Shastra',
    'Kundli Analysis', 'Horoscope Reading', 'Gun Milan', 'Numerology',
    'Gemstone Recommendation', 'Muhurat', 'Daily Panchang',
    'Mangal Dosha', 'Sade Sati', 'Spiritual Guidance', 'Remedies',
  ],
  knowsLanguage: ['hi', 'en', 'sa'],
  hasOccupation: {
    '@type': 'Occupation',
    name: 'Vedic Astrologer',
    occupationLocation: { '@type': 'City', name: 'Faridabad', addressCountry: 'IN' },
  },
  address: { '@type': 'PostalAddress', addressLocality: 'Faridabad', addressRegion: 'Haryana', addressCountry: 'IN' },
  sameAs: VIVEK_SAME_AS,
});

// ─────────────────────────────────────────────────────────────────────────────
// Organization Schema — ViratAstro
// ─────────────────────────────────────────────────────────────────────────────
export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': ORG_ID,
  name: SITE_NAME,
  legalName: 'ViratAstro',
  url: SITE_URL,
  logo: `${SITE_URL}/va-logo-watermark.png`,
  image: DEFAULT_IMAGE,
  description: 'ViratAstro — premier Vedic astrology, Parashari Jyotish, and Vastu consultancy founded by Acharya Vivek Virat Mudgal. Free Kundli, Daily Panchang, Pucho Guruji AI consultations, and expert remedies.',
  founder: { '@id': VIVEK_ID },
  foundingDate: '1992',
  foundingLocation: { '@type': 'Place', name: 'Faridabad, India' },
  knowsLanguage: ['hi', 'en'],
  areaServed: ['IN', 'AE', 'US', 'GB', 'CA', 'SG', 'AU', 'Worldwide'],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+91-9311369729',
    contactType: 'customer support',
    availableLanguage: ['Hindi', 'English'],
  },
  sameAs: VIVEK_SAME_AS,
});

// ─────────────────────────────────────────────────────────────────────────────
// WebSite Schema — with SearchAction (sitelinks search box eligibility)
// ─────────────────────────────────────────────────────────────────────────────
export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: SITE_URL,
  name: SITE_NAME,
  publisher: { '@id': ORG_ID },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
  inLanguage: ['hi-IN', 'en-IN'],
});

// ─────────────────────────────────────────────────────────────────────────────
// Article Schema — for blog posts (full EEAT with author/publisher refs)
// ─────────────────────────────────────────────────────────────────────────────
export const articleSchema = (blog) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  '@id': `${SITE_URL}/blog/${blog.slug}#article`,
  mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${blog.slug}` },
  headline: (blog.title || '').slice(0, 110),
  description: (blog.excerpt || blog.meta_description || '').slice(0, 250),
  image: blog.image_url || blog.image || DEFAULT_IMAGE,
  author: { '@id': VIVEK_ID },
  publisher: { '@id': ORG_ID },
  datePublished: blog.created_at || blog.published_at || blog.datePublished,
  dateModified: blog.updated_at || blog.created_at || blog.dateModified,
  keywords: Array.isArray(blog.keywords) ? blog.keywords.join(', ')
           : (blog.keywords || `${blog.category || 'Astrology'}, Vedic Astrology, ViratAstro`),
  articleSection: blog.category || 'Astrology',
  inLanguage: blog.language || 'hi-IN',
  // Speakable schema — Google Assistant / TTS read-aloud
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ['h1', 'h2', '.blog-excerpt', '.blog-content > p:first-of-type'],
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// FAQPage Schema — auto-generated when FAQs array present
// ─────────────────────────────────────────────────────────────────────────────
export const faqPageSchema = (faqs) => {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a, question, answer }) => ({
      '@type': 'Question',
      name: q || question,
      acceptedAnswer: { '@type': 'Answer', text: a || answer },
    })),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// AboutPage Schema
// ─────────────────────────────────────────────────────────────────────────────
export const aboutPageSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': `${SITE_URL}/about#aboutpage`,
  url: `${SITE_URL}/about`,
  about: { '@id': VIVEK_ID },
  mainEntity: { '@id': VIVEK_ID },
  publisher: { '@id': ORG_ID },
});

// ─────────────────────────────────────────────────────────────────────────────
// LocalBusiness Schema — "Vivek Mudgal Astrologer" (Faridabad practice)
// Linked to Organization + Person via @id for full entity graph.
// ─────────────────────────────────────────────────────────────────────────────
export const localBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': ['LocalBusiness', 'ProfessionalService'],
  '@id': LOCAL_BUSINESS_ID,
  name: 'Vivek Mudgal Astrologer',
  alternateName: ['ViratAstro', 'Acharya Vivek Mudgal'],
  url: SITE_URL,
  image: VIVEK_PHOTO,
  logo: `${SITE_URL}/va-logo-watermark.png`,
  description: 'Vivek Mudgal Astrologer (ViratAstro) — Vedic Astrology, Parashari Jyotish, Vastu Consultation, Kundli Analysis and traditional Astrology Remedies by Acharya Vivek Virat Mudgal. 33+ years experience. Featured on News Nation.',
  founder: { '@id': VIVEK_ID },
  parentOrganization: { '@id': ORG_ID },
  telephone: '+91-9311369729',
  email: 'vivek.mudgal.vm@gmail.com',
  priceRange: '₹99 — ₹5,100',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Faridabad',
    addressRegion: 'Haryana',
    postalCode: '121001',
    addressCountry: 'IN',
  },
  areaServed: [
    { '@type': 'Country', name: 'India' },
    { '@type': 'Country', name: 'United Arab Emirates' },
    { '@type': 'Country', name: 'United States' },
    { '@type': 'Country', name: 'United Kingdom' },
    { '@type': 'Country', name: 'Canada' },
    { '@type': 'Country', name: 'Singapore' },
    { '@type': 'Country', name: 'Australia' },
    { '@type': 'AdministrativeArea', name: 'Worldwide' },
  ],
  serviceType: [
    'Vedic Astrology',
    'Parashari Jyotish',
    'Vastu Consultation',
    'Kundli Analysis',
    'Astrology Remedies',
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Astrology Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Vedic Astrology Consultation', provider: { '@id': VIVEK_ID } } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Parashari Jyotish Reading',  provider: { '@id': VIVEK_ID } } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Vastu Consultation',         provider: { '@id': VIVEK_ID } } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kundli Analysis',            provider: { '@id': VIVEK_ID } } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Astrology Remedies (Upay)',  provider: { '@id': VIVEK_ID } } },
    ],
  },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    opens: '09:00',
    closes: '21:00',
  },
  knowsLanguage: ['Hindi', 'English', 'Sanskrit'],
  sameAs: VIVEK_SAME_AS,
});

// ─────────────────────────────────────────────────────────────────────────────
// Site-wide bundle — Person + Organization + WebSite + LocalBusiness together
// Include this on every major page (home, about, blog, services) so that
// AI engines / Google Knowledge Graph can resolve entities globally.
// ─────────────────────────────────────────────────────────────────────────────
export const siteWideSchemas = () => [
  organizationSchema(),
  websiteSchema(),
  personSchema(),
  localBusinessSchema(),
];
